<template>
  <b-modal
    :active="displayModal"
    has-modal-card
    @close="$emit('close')"
    :can-cancel="['escape', 'x', 'outside']"
    :width="1200"
  >
    <div class="modal-card" style="width: 1200px">
      <form @submit.prevent="validateBeforeSubmit">
        <header class="modal-card-head">
          <p class="modal-card-title">{{ modalTitle }}</p>
        </header>
        <section class="modal-card-body">
          <div class="columns">
            <div class="column is-one-quarter">
              <b-menu>
                <b-menu-list>
                  <b-menu-item
                    v-for="(category, indexes) in getCategories()"
                    :key="indexes"
                    :expanded="category === cractivity.category"
                    :active="category === cractivity.category"
                    @click="changeCategory(category)"
                  >
                    <template slot="label" slot-scope="props">
                      {{ $t(category) }}
                      <b-icon
                        class="is-pulled-right"
                        :icon="props.expanded ? 'caret-down' : 'caret-up'"
                      >
                      </b-icon>
                    </template>
                    <b-menu-item
                      v-for="(type, index) in getTypes(category)"
                      :key="index"
                      :label="$t(type)"
                      :active="type === cractivity.type"
                      @click="changeType(type)"
                    />
                  </b-menu-item>
                </b-menu-list>
              </b-menu>
            </div>
            <div class="column">
              <component
                v-if="cractivity.type"
                :is="importName(cractivity.type)"
              />
              <b-notification :closable="false">
                <component
                  v-if="hasInputComponent"
                  :is="getComponentName(cractivity.type)"
                  :ractivity="cractivity"
                  :disabled="isSubmitted"
                />
                <default-input
                  v-else
                  :ractivity="cractivity"
                  :disabled="isSubmitted"
                />
              </b-notification>
              <b-notification
                v-if="authStore.isAuthenticated"
                :closable="false"
              >
                <tags-input v-model="cractivity.tags" :disabled="isSubmitted" />
              </b-notification>
            </div>
          </div>
          <hr />
          <nav class="level">
            <div class="level-left" />
            <div class="buttons level-right">
              <b-button
                class="button"
                @click="$emit('close')"
                icon-left="close"
              >
                {{ $t('cancel') }}
              </b-button>
              <button
                v-show="modeCreation"
                type="submit"
                :disabled="isSubmitted"
                class="button is-primary"
                data-testId="add-ractivity"
              >
                <b-icon size="is-small" icon="plus" />
                <span> {{ $t('add') }} </span>
              </button>
              <button
                v-show="!modeCreation"
                :disabled="isSubmitted"
                type="submit"
                class="button is-primary"
              >
                <span class="icon is-small"><i class="fa fa-refresh"></i></span>
                <span> {{ $t('update') }} </span>
              </button>
            </div>
          </nav>
        </section>
      </form>
    </div>
  </b-modal>
</template>

<script>
import Vue from 'vue'
import { useGHGIStore } from '@/stores/ghgi.js'
import { useAuthenticationStore } from '@/stores/authentication.js'
import {
  researchActivityClass,
  researchActivityFactory,
  ResearchActivity
} from '@/models/carbon/ResearchActivity.js'

export default {
  name: 'Modal',
  props: {
    displayModal: {
      type: Boolean,
      default: false
    },
    ractivity: {
      type: Object,
      default: function () {
        return {}
      }
    },
    modeCreation: {
      type: Boolean,
      default: false
    }
  },
  $_veeValidate: {
    validator: 'new'
  },
  components: {
    'default-input': () =>
      import('@/components/carbon/forms/ractivities/DefaultInput.vue'),
    'tags-input': () => import('@/components/core/TagsInput.vue')
  },
  setup () {
    const ghgiStore = useGHGIStore()
    const authStore = useAuthenticationStore()
    return { ghgiStore, authStore }
  },
  data () {
    return {
      cractivity: Object.assign({}, this.ractivity),
      hasInputComponent: false,
      error: null
    }
  },
  computed: {
    year: function () {
      return parseInt(this.ghgiStore.item.year)
    },
    isSubmitted: function () {
      return this.ghgiStore.item.isSubmitted
    },
    modalTitle: function () {
      let title = this.$t('compCarbonFormsRactivitiesModal.title-1')
      if (this.modeCreation) {
        title = this.$t('compCarbonFormsRactivitiesModal.title-2')
      } else if (this.isSubmitted) {
        title = this.$t('compCarbonFormsRactivitiesModal.title-3')
      }
      return title
    }
  },
  methods: {
    getCategories: function () {
      return ResearchActivity.getCategories()
    },
    getTypes: function (category) {
      return ResearchActivity.getTypes(category)
    },
    getSubTypes: function (category, type) {
      let cclass = researchActivityClass(type)
      return cclass.getSubTypes(category, type, this.year)
    },
    getComponentName: function (type) {
      let componentName = null
      if (type) {
        let parts = type.split('.')
        componentName = parts
          .map((val) => {
            return val.charAt(0).toUpperCase() + val.slice(1)
          })
          .join('')
      }
      return componentName
    },
    getDirectoryName: function (type) {
      return type.split('.').join('')
    },
    updateHasInputComponent: function () {
      let self = this
      this.hasInputComponent = false
      let componentName = this.getComponentName(this.cractivity.type)
      let directoryName = this.getDirectoryName(this.cractivity.type)
      if (componentName) {
        ;(async function load () {
          let cpt = await import(
            '@/components/carbon/forms/ractivities/components/' +
              directoryName +
              '/Input.vue'
          ).catch((err) => {
            self.error = err
          })
          if (cpt !== undefined) {
            Vue.component(componentName, () =>
              import(
                '@/components/carbon/forms/ractivities/components/' +
                  directoryName +
                  '/Input.vue'
              )
            )
          }
          return cpt !== undefined
        })().then((val) => {
          this.hasInputComponent = val
        })
      }
    },
    changeCategory: function (category) {
      let type = this.getTypes(category)[0]
      this.cractivity = researchActivityFactory({
        id: null,
        category: category,
        type: type,
        subtype: null
      })
      this.updateHasInputComponent()
    },
    changeType: function (type) {
      this.cractivity = researchActivityFactory({
        id: null,
        category: this.cractivity.category,
        type: type,
        subtype: null
      })
      this.updateHasInputComponent()
    },
    validateBeforeSubmit () {
      this.$validator.validateAll().then((result) => {
        if (result) {
          if (this.modeCreation) {
            this.$emit('add', this.cractivity)
          } else {
            this.$emit('update', {
              from: this.ractivity,
              to: this.cractivity
            })
          }
        }
      })
    },
    importName: function (type) {
      return this.getComponentName(type) + 'Doc'
    }
  },
  mounted () {
    if (this.modeCreation) {
      let category = this.getCategories()[0]
      let type = this.getTypes(category)[0]
      this.cractivity = researchActivityFactory({
        id: null,
        category: category,
        type: type,
        subtype: null
      })
    }
    this.updateHasInputComponent()
  },
  beforeMount () {
    for (let category of this.getCategories()) {
      for (let type of this.getTypes(category)) {
        Vue.component(this.importName(type), () =>
          import(
            '@/components/carbon/forms/ractivities/components/' +
              this.getDirectoryName(type) +
              '/CardHelp.vue'
          )
        )
      }
    }
  }
}
</script>
